<template>
	<div>
		<h5 class="text-h5 q-mt-lg q-mb-md">{{ $t(game_data.game_name) }}</h5>

		<q-collapsible
			class="shadow-2 q-pa-sm q-ma-xs rounded-border"
			icon="far fa-ballot-check"
			right-icon="far fa-ballot-check"
			:label="$t('how_to_create_the_game')"
		>
			<div class="q-ma-sm">
				<p>{{ $t('true_or_false_instructions') }}</p>
				<p class="q-mt-lg">
					<em
						><strong>{{ `${$t('warning')}: ` }}</strong
						>{{ $t('create_game_before_save') }}</em
					>
				</p>
			</div>
		</q-collapsible>

		<div class="row">
			<div class="col-12 q-mb-md">
				<div class="row gutter-sm">
					<div class="col-12">
						<q-list
							v-if="game_data.options.length"
							dense
							bordered
							padding
							separator
							class="q-mt-md"
						>
							<q-item v-for="(option, index) in game_data.options" :key="index">
								<q-item-main class="q-mx-sm q-mt-sm row">
									<q-field :count="250" class=" col q-px-sm">
										<q-input
											v-model="option.question.text"
											type="textarea"
											rows="1"
											:max-height="250"
											:float-label="$t('phrase')"
											@change="game_built ? resetGame() : null"
											:error="
												$v.game_data.options.$each[index].question.text
													.$error
											"
											@input="
												$v.game_data.options.$each[index].question.text
													.$touch;
											"
										/>
									</q-field>
									<q-field class="col-auto q-my-sm">
										<div class="row justify-center">
											<div>
												<q-btn-toggle
													v-model="option.correct_choice"
													:options="selectors"
												/>
												<q-icon
													v-if="
														option.question.length &&
															option.question.text.length
													"
													name="fal fa-check"
													color="positive"
													class="q-mx-md"
												/>
												<q-btn
													v-if="game_data.options.length > 1"
													round
													flat
													color="negative"
													icon="fal fa-trash"
													@click="removeWord(index)"
												>
													<q-tooltip>
														{{ $t('remove') }}
													</q-tooltip>
												</q-btn>
											</div>
										</div>
									</q-field>
								</q-item-main>
							</q-item>
						</q-list>

						<div class="col-xs-12 col-sm-4 q-mt-sm q-mx-xs">
							<q-field :helper="$t('leave_it_blank_or_negative_to_use_infinite')">
								<q-input
									v-model="game_data.max_errors"
									type="number"
									:float-label="$t('alternative_error_limit')"
									@change="game_built ? resetGame() : null"
								/>
							</q-field>
						</div>

						<div class="row justify-end q-mr-md q-mt-md">
							<q-btn
								class="q-mr-md"
								color="primary"
								icon="far fa-plus"
								:label="$t('add')"
								@click="addOption"
								:disable="game_data.options.length > 9"
							/>
							<q-btn
								color="secondary"
								:label="$t('preview')"
								@click="buildGamePreview"
								:disable="disableGamePreview"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>

		<true-or-false-preview
			v-if="show_game_preview"
			:show_game_preview="show_game_preview"
			@hide_preview="hidePreview()"
			:game_name="game_data.game_name"
			:game_options="game_data.options"
			:game_max_errors="game_data.max_errors"
			:selectors="selectors"
		/>
	</div>
</template>

<script>
import { required, maxLength } from 'vuelidate/lib/validators'
import TrueOrFalsePreview from './TrueOrFalsePreview.vue'

export default {
	name: 'AdminGameTrueOrFalse',

	props: ['can_save', 'saved_game_data', 'id_game'],

	components: {
		'true-or-false-preview': TrueOrFalsePreview,
	},

	data() {
		return {
			game_data: {
				game_name: 'true_or_false',
				max_errors: null,
				options: [
					{
						question: { text: '', value: 1 },
						correct_choice: 1,
						correct: undefined,
						position: 0,
						selected: null,
					},
				],
			},
			selectors: [
				{ label: this.$t('true'), value: 1 },
				{ label: this.$t('false'), value: 0 },
			],
			show_game_preview: false,
			game_built: false,
		}
	},

	computed: {
		disableGamePreview() {
			return this.game_data.options.length >= 1 ? false : true
		},
	},

	methods: {
		buildGamePreview() {
			this.$v.$touch()
			if (!this.$v.$error) {
				this.show_game_preview = true
				this.game_built = true
				this.$emit('save_toggle', this.game_built)
				this.$emit('save_game_data', this.game_data)
			}
		},

		resetGame() {
			this.game_built = false
			this.$emit('save_toggle', this.game_built)
			this.$emit('save_game_data', null)
		},

		hidePreview() {
			this.show_game_preview = false
		},

		addOption() {
			const newSlot = {
				question: { text: '', value: 1 },
				correct_choice: 1,
				correct: undefined,
				position: 0,
				selected: null,
			}

			this.game_data.options.push(newSlot)
			if (this.game_built) {
				this.resetGame()
			}
		},

		removeWord(index) {
			this.game_data.options.splice(index, 1)
			if (this.game_built) this.resetGame()
		},
	},

	mounted() {
		if (this.saved_game_data) {
			this.game_data = this.saved_game_data
		} else {
			this.game_data = {
				game_name: 'true_or_false',
				max_errors: null,
				options: [
					{
						question: { text: '', value: 1 },
						correct_choice: 1,
						correct: undefined,
						position: 0,
						selected: null,
					},
				],
			}
		}
	},

	beforeDestroy() {
		this.resetGame()
	},

	validations() {
		return {
			game_data: {
				options: {
					$each: {
						question: { text: { required, maxLength: maxLength(250) } },
					},
				},
			},
		}
	},
}
</script>

<style></style>
